import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { getReminderSettings, updateReminderSettings } from '../../services/api';
import { Bell, Clock, Plus, Trash, Eye, EyeSlash } from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export function RemindersSettings() {
  const [reminderSettings, setReminderSettings] = useState(null);
  const [savingReminders, setSavingReminders] = useState(false);
  const [brevoKeyInput, setBrevoKeyInput] = useState('');
  const [showBrevoKey, setShowBrevoKey] = useState(false);

  useEffect(() => { fetchReminderSettings(); }, []);

  const fetchReminderSettings = async () => { try { const res = await getReminderSettings(); setReminderSettings(res.data); } catch {} };

  const handleToggleReminderEnabled = async (enabled) => {
    setSavingReminders(true);
    try { await updateReminderSettings({ enabled }); setReminderSettings(prev => ({ ...prev, enabled })); toast.success(enabled ? 'Relances activees' : 'Relances desactivees'); }
    catch { toast.error('Erreur'); } finally { setSavingReminders(false); }
  };

  const handleUpdateDelay = async (idx, field, value) => {
    const newDelays = [...(reminderSettings?.delays || [])];
    newDelays[idx] = { ...newDelays[idx], [field]: value };
    setSavingReminders(true);
    try { await updateReminderSettings({ delays: newDelays }); setReminderSettings(prev => ({ ...prev, delays: newDelays })); toast.success('Regle mise a jour'); }
    catch { toast.error('Erreur'); } finally { setSavingReminders(false); }
  };

  const handleAddDelay = async () => {
    const newDelays = [...(reminderSettings?.delays || []), { key: `custom_${Date.now()}`, days: 1, label: 'J+1', email: true, sms: false }];
    setSavingReminders(true);
    try { await updateReminderSettings({ delays: newDelays }); setReminderSettings(prev => ({ ...prev, delays: newDelays })); toast.success('Regle ajoutee'); }
    catch { toast.error('Erreur'); } finally { setSavingReminders(false); }
  };

  const handleRemoveDelay = async (idx) => {
    const newDelays = (reminderSettings?.delays || []).filter((_, i) => i !== idx);
    setSavingReminders(true);
    try { await updateReminderSettings({ delays: newDelays }); setReminderSettings(prev => ({ ...prev, delays: newDelays })); toast.success('Regle supprimee'); }
    catch { toast.error('Erreur'); } finally { setSavingReminders(false); }
  };

  const handleSaveBrevo = async () => {
    setSavingReminders(true);
    try {
      const updates = { brevo_sender: reminderSettings?.brevo_sender || 'OSCOP' };
      if (brevoKeyInput) updates.brevo_api_key = brevoKeyInput;
      await updateReminderSettings(updates);
      setBrevoKeyInput('');
      fetchReminderSettings();
      toast.success('Configuration Brevo sauvegardee');
    } catch { toast.error('Erreur'); } finally { setSavingReminders(false); }
  };

  return (
    <Card className="border-[#E8E6E1]" data-testid="reminder-settings-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <Bell size={18} className="text-[#00AEEF]" /> Relances automatiques
          </CardTitle>
          <div className="flex items-center gap-2">
            <span className="text-xs text-[#5E6D66]">{reminderSettings?.enabled ? 'Actif' : 'Inactif'}</span>
            <Switch checked={reminderSettings?.enabled || false} onCheckedChange={handleToggleReminderEnabled} data-testid="reminder-enabled-toggle" />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Brevo SMS config */}
        <div className="p-4 rounded-lg border border-[#E8E6E1] space-y-3">
          <p className="text-xs font-semibold text-[#1A2421] uppercase">Configuration SMS (Brevo)</p>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5 col-span-2">
              <Label className="text-xs">Cle API Brevo</Label>
              <div className="relative">
                <Input type={showBrevoKey ? 'text' : 'password'} value={brevoKeyInput} onChange={(e) => setBrevoKeyInput(e.target.value)} placeholder={reminderSettings?.brevo_api_key_masked || 'xkeysib-...'} className="text-xs font-mono pr-10" data-testid="brevo-api-key-input" />
                <button type="button" onClick={() => setShowBrevoKey(!showBrevoKey)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#5E6D66]">{showBrevoKey ? <EyeSlash size={14} /> : <Eye size={14} />}</button>
              </div>
              <p className="text-[10px] text-[#5E6D66]">Obtenez votre cle sur app.brevo.com &gt; Parametres &gt; Cles API</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Nom expediteur SMS</Label>
              <Input value={reminderSettings?.brevo_sender || ''} onChange={(e) => setReminderSettings(prev => ({ ...prev, brevo_sender: e.target.value.slice(0, 11) }))} maxLength={11} className="text-xs" data-testid="brevo-sender-input" />
              <p className="text-[10px] text-[#5E6D66]">Max 11 caracteres</p>
            </div>
          </div>
          <Button onClick={handleSaveBrevo} disabled={savingReminders} size="sm" className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="save-brevo-btn">
            {savingReminders ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null} Sauvegarder Brevo
          </Button>
        </div>

        {/* Delay rules */}
        <div className="p-4 rounded-lg border border-[#E8E6E1] space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-[#1A2421] uppercase">Regles de relance</p>
            <Button variant="ghost" size="sm" onClick={handleAddDelay} className="h-7 text-xs text-[#2D5A4C]" data-testid="add-delay-rule-btn"><Plus size={12} className="mr-1" /> Ajouter</Button>
          </div>
          <div className="space-y-2">
            {(reminderSettings?.delays || []).map((delay, idx) => (
              <div key={delay.key || idx} className="flex items-center gap-2 p-2.5 rounded-lg bg-[#F9F8F5]" data-testid={`delay-rule-${idx}`}>
                <div className="flex items-center gap-1.5 min-w-[100px]">
                  <Clock size={14} className="text-[#5E6D66]" />
                  <Input type="number" value={delay.days} onChange={(e) => { const days = parseInt(e.target.value); const label = days < 0 ? `J${days}` : days === 0 ? 'Jour J' : `J+${days}`; handleUpdateDelay(idx, 'days', days); handleUpdateDelay(idx, 'label', label); }} className="h-7 w-16 text-xs text-center" />
                  <span className="text-xs text-[#5E6D66] font-medium">{delay.label}</span>
                </div>
                <div className="flex items-center gap-3 ml-4">
                  <label className="flex items-center gap-1.5 cursor-pointer"><Switch checked={delay.email} onCheckedChange={(v) => handleUpdateDelay(idx, 'email', v)} className="scale-75" /><span className="text-xs text-[#5E6D66]">Email</span></label>
                  <label className="flex items-center gap-1.5 cursor-pointer"><Switch checked={delay.sms} onCheckedChange={(v) => handleUpdateDelay(idx, 'sms', v)} className="scale-75" /><span className="text-xs text-[#5E6D66]">SMS</span></label>
                </div>
                <button onClick={() => handleRemoveDelay(idx)} className="ml-auto text-red-400 hover:text-red-600 p-1"><Trash size={14} /></button>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-[#5E6D66]">Jours negatifs = avant l'echeance, 0 = jour J, positifs = apres l'echeance (retard)</p>
        </div>
      </CardContent>
    </Card>
  );
}
